import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../components/Config/api.service';
import { Modal } from '../../components/Utils/ModalShow';

/**
 * Modifier ou annuler une demande d'argent avant toute décision
 * (Yros/Kavero#492). Le serveur juge : ses refus (plafond, mois clos, décision
 * déjà prise) s'affichent tels quels et la boîte reste ouverte.
 */

const MOTIFS_PRET = ['famille', 'logement', 'scolarite', 'equipement', 'autre'];

const messageDe = (e, t) => e?.response?.data?.message || t('retoucheDemande.erreurGenerique');

const CHAMP = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[--color-primary]';

function Champ({ label, children }) {
  return (
    <label className="block text-sm text-slate-700">
      <span className="block mb-1 font-medium">{label}</span>
      {children}
    </label>
  );
}

export function ModifierDemandeArgent({ demande, onFermer, onModifiee }) {
  const { t } = useTranslation();
  const contenu = demande.contenu || {};
  const [montant, setMontant] = useState(String(contenu.montant ?? ''));
  const [mois, setMois] = useState(contenu.mois_retenue || '');
  const [debut, setDebut] = useState(contenu.debut || '');
  const [fin, setFin] = useState(contenu.fin || '');
  const [motif, setMotif] = useState(contenu.motif || '');
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const enregistrer = async () => {
    const nombre = Number(String(montant).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(nombre) || nombre <= 0) {
      setErreur(t('retoucheDemande.montantInvalide'));
      return;
    }
    const nouveau = { montant: nombre };
    if (demande.type === 'avance') nouveau.mois_retenue = mois;
    if (demande.type === 'pret') Object.assign(nouveau, { debut, fin, motif });
    setEnvoi(true);
    setErreur(null);
    try {
      await apiService.modifierDemandeRh(demande.id, demande.type, nouveau);
      onModifiee();
    } catch (e) {
      setErreur(messageDe(e, t));
    } finally {
      setEnvoi(false);
    }
  };

  const pied = (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onFermer} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
        {t('retoucheDemande.annuler')}
      </button>
      <button type="button" data-testid="retouche-enregistrer" onClick={enregistrer} disabled={envoi}
        className="px-4 py-2 text-sm rounded-lg bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50">
        {t('retoucheDemande.enregistrer')}
      </button>
    </div>
  );

  return (
    <Modal isOpen onClose={onFermer} title={t('retoucheDemande.titreModifier')} size={480} footer={pied}>
      <div className="space-y-3" data-testid="retouche-formulaire">
        <Champ label={t('retoucheDemande.montant')}>
          <input data-testid="retouche-montant" type="text" inputMode="decimal" value={montant}
            onChange={(e) => setMontant(e.target.value)} className={CHAMP} />
        </Champ>
        {demande.type === 'avance' && (
          <Champ label={t('retoucheDemande.moisRetenue')}>
            <input data-testid="retouche-mois" type="month" value={mois} onChange={(e) => setMois(e.target.value)} className={CHAMP} />
          </Champ>
        )}
        {demande.type === 'pret' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Champ label={t('demandePret.debut')}>
                <input data-testid="retouche-debut" type="month" value={debut} onChange={(e) => setDebut(e.target.value)} className={CHAMP} />
              </Champ>
              <Champ label={t('demandePret.fin')}>
                <input data-testid="retouche-fin" type="month" value={fin} onChange={(e) => setFin(e.target.value)} className={CHAMP} />
              </Champ>
            </div>
            <Champ label={t('demandePret.motif')}>
              <select data-testid="retouche-motif" value={motif} onChange={(e) => setMotif(e.target.value)} className={CHAMP}>
                {MOTIFS_PRET.map((m) => <option key={m} value={m}>{t(`demandePret.motifs.${m}`)}</option>)}
              </select>
            </Champ>
          </>
        )}
        {erreur && (
          <p role="alert" data-testid="retouche-erreur" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {erreur}
          </p>
        )}
      </div>
    </Modal>
  );
}

export function AnnulerDemandeArgent({ demande, onFermer, onAnnulee }) {
  const { t } = useTranslation();
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const confirmer = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      await apiService.annulerDemandeRh(demande.id, demande.type);
      onAnnulee();
    } catch (e) {
      setErreur(messageDe(e, t));
    } finally {
      setEnvoi(false);
    }
  };

  const pied = (
    <div className="flex justify-end gap-2">
      <button type="button" onClick={onFermer} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
        {t('retoucheDemande.garder')}
      </button>
      <button type="button" data-testid="retouche-confirmer-annulation" onClick={confirmer} disabled={envoi}
        className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
        {t('retoucheDemande.confirmerAnnulation')}
      </button>
    </div>
  );

  return (
    <Modal isOpen onClose={onFermer} title={t('retoucheDemande.titreAnnuler')} size={440} footer={pied}>
      <p className="text-sm text-slate-700">{t('retoucheDemande.questionAnnuler', {
        type: t(`mesAvances.types.${demande.type}`, { defaultValue: demande.type }) })}</p>
      {erreur && (
        <p role="alert" data-testid="retouche-erreur" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {erreur}
        </p>
      )}
    </Modal>
  );
}
